import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate } from '@tanstack/react-router';
import { Eye } from 'lucide-react';
import { useContext, useEffect, useMemo, useRef, useState } from 'react';

import type { TransactionClaimsOut } from '@/api/claimedLines';
import { approveFinding, listFindingEvents, needsReviewFinding, rejectFinding } from '@/api/findings';
import type { InvoiceExtractionOut, InvoiceLinkOut } from '@/api/invoices';
import { markNoInvoice, unmarkNoInvoice } from '@/api/invoices';
import type { FindingOut, ReasonCode, RuleOut } from '@/api/types';
import { BULK_APPROVAL_REASON, defaultApprovalReason } from '@/lib/approval-reasons';
import { AttachmentChip } from '@/components/AttachmentChip';
import { CarriedForwardPill } from '@/components/CarriedForwardPill';
import { ClaimedGstPanel } from '@/components/ClaimedGstPanel';
import { FindingTags } from '@/components/FindingTags';
import { JournalEntryPanel } from '@/components/JournalEntryPanel';
import { ReasonCodePills, ReasonCodesPicker } from '@/components/ReasonCodesPicker';
import { RecoveryAmountInline } from '@/components/RecoveryAmountInline';
import { RunCustomerSlugContext } from '@/components/RunCustomerSlugContext';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardFooter,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { StatusBadge as FindingStatusBadge } from '@/components/ui/status-badge';
import { dispositionTrail } from '@/lib/dispositionHistory';
import { tagsFor } from '@/lib/finding-tags';
import type { Currency } from '@/lib/format';
import { coerceCurrency, formatCurrency } from '@/lib/format';
import { clickTargetIsInteractive } from '@/lib/rowLink';
import { cn } from '@/lib/utils';
import { useAuth } from '../auth/AuthContext';

function stringOrNull(value: unknown): string | null {
  if (typeof value !== 'string') return null;
  return value.length > 0 ? value : null;
}

export function FindingCard({
  finding,
  rule,
  rulesLoading,
  rulesError,
  canReview,
  runId,
  onAfterReview,
  pendingBySupplier,
  onBulkForSupplier,
  onOpenSupplier,
  invoiceLinks,
  isMarkedNoInvoice,
  invoiceExtractions,
  claims,
  claimPeriodCount = 0,
  claimsReady = false,
}: {
  finding: FindingOut;
  rule: RuleOut | null;
  rulesLoading: boolean;
  rulesError: boolean;
  canReview: boolean;
  runId: string;
  onAfterReview: () => void;
  pendingBySupplier: Map<string, number>;
  onBulkForSupplier: (
    supplier: string,
    action: 'approve' | 'reject' | 'needs_review',
  ) => void;
  /** Called when the practitioner clicks the supplier name on this
   *  card. Opens the supplier page, routing by the finding's supplier id
   *  when present so a `/` in the name can't break the URL. */
  onOpenSupplier: (supplierName: string, supplierId: string | null) => void;
  /** Invoice documents attached to this finding's transaction (any
   *  run). Undefined = none. */
  invoiceLinks?: InvoiceLinkOut[];
  /** True when the transaction is marked "no invoice available". */
  isMarkedNoInvoice?: boolean;
  /** Document-level labels keyed by document id. */
  invoiceExtractions?: Map<string, InvoiceExtractionOut>;
  /** Deterministic filed-claim lines for this finding's transaction.
   *  Undefined = none. */
  claims?: TransactionClaimsOut;
  /** Distinct filed periods loaded for the customer — 0 (the default)
   *  hides the claimed-GST section entirely. */
  claimPeriodCount?: number;
  /** True once the batched claimed-lines lookup resolved. */
  claimsReady?: boolean;
}) {
  const { userKey, getIdToken } = useAuth();
  const cardCustomerSlug = useContext(RunCustomerSlugContext);
  const qc = useQueryClient();
  const navigate = useNavigate();
  const goToDetail = () => {
    void navigate({
      to: '/runs/$id/findings/$findingId',
      params: { id: runId, findingId: finding.finding_id },
    });
  };
  const [note, setNote] = useState('');
  // Reason codes attached to the rejection. Reset alongside the note
  // after any disposition mutation so the card is clean for the next
  // finding rendered in its place.
  const [rejectCodes, setRejectCodes] = useState<ReasonCode[]>([]);

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ['findings', 'by-run', runId, userKey] });
    qc.invalidateQueries({ queryKey: ['runs', runId, userKey] });
    qc.invalidateQueries({ queryKey: ['finding-events', finding.finding_id] });
  };

  // The "no invoice available" mark (INP-494) — a triage state on the
  // transaction, orthogonal to disposition. Toggling refreshes the
  // attachment lookup so the pill, list badge, and worklist update.
  const noInvoiceMark = useMutation({
    mutationFn: async (marked: boolean) => {
      const token = (await getIdToken()) ?? '';
      const slug = cardCustomerSlug ?? '';
      return marked
        ? markNoInvoice(slug, finding.transaction_id, token)
        : unmarkNoInvoice(slug, finding.transaction_id, token);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['transaction-documents', cardCustomerSlug] });
    },
  });

  const approve = useMutation({
    mutationFn: async () => {
      const trimmed = note.trim();
      // Rapid-review surface: the basis is inferred, not picked — an
      // attached invoice showing tax confirms it; otherwise a quick approve
      // without opening an invoice is the sampled/extrapolated case. The
      // deliberate detail-page panel offers the explicit picker. INP-636.
      const reason = defaultApprovalReason(invoiceLinks) ?? BULK_APPROVAL_REASON;
      return approveFinding(
        finding.finding_id,
        (await getIdToken()) ?? '',
        reason,
        trimmed.length > 0 ? trimmed : undefined,
      );
    },
    onSuccess: () => {
      setNote('');
      setRejectCodes([]);
      invalidate();
      onAfterReview();
    },
  });

  const reject = useMutation({
    mutationFn: async () =>
      rejectFinding(finding.finding_id, note.trim(), (await getIdToken()) ?? '', rejectCodes),
    onSuccess: () => {
      setNote('');
      setRejectCodes([]);
      invalidate();
      onAfterReview();
    },
  });

  const needsReview = useMutation({
    mutationFn: async () =>
      needsReviewFinding(finding.finding_id, note.trim(), (await getIdToken()) ?? ''),
    onSuccess: () => {
      setNote('');
      setRejectCodes([]);
      invalidate();
      onAfterReview();
    },
  });

  const trimmedNote = note.trim();
  // Reject takes EITHER a free-text note OR one+ structured codes
  // (or both). The backend's FindingRejectIn enforces the same shape.
  // Needs-review still requires a note — there's no structured
  // equivalent yet.
  const canSubmitReject = trimmedNote.length > 0 || rejectCodes.length > 0;
  const canSubmitNeedsReview = trimmedNote.length > 0;

  const pendingMutation =
    approve.isPending ||
    reject.isPending ||
    needsReview.isPending;
  // Practitioners can always re-review — approve/reject buttons stay
  // available on already-reviewed findings so a status can be flipped
  // (the backend allows it; see finding_repo._transition_status).
  const reviewable = canReview;

  // Ref for the rejection-note textarea — `r` focuses it.
  const noteRef = useRef<HTMLTextAreaElement | null>(null);

  // Card-level keyboard shortcuts. Disposition keys (`a`, `r`) skip
  // when focus is in a textarea — the practitioner is typing a note
  // and a stray `a` shouldn't fire an approve. Navigation lives at
  // the parent (FindingsReviewQueue) level.
  useEffect(() => {
    if (!reviewable) return;
    const handler = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement | null;
      const tag = target?.tagName;
      const inTextField = tag === 'TEXTAREA' || tag === 'INPUT' || tag === 'SELECT';

      // Defensive: never claim a keystroke that has a Ctrl/Cmd/Alt
      // modifier. `e.key` reports the bare character, so without this
      // guard `e.key === 'a'` would also fire on ⌘A (select-all), `e.key
      // === 'r'` on ⌘R (reload), etc. The OS gesture wins — engine
      // shortcuts are plain-key only.
      if (e.ctrlKey || e.metaKey || e.altKey) return;

      if (inTextField) return;
      if (pendingMutation) return;

      if (e.key === 'a') {
        approve.mutate();
        e.preventDefault();
      } else if (e.key === 'r') {
        noteRef.current?.focus();
        e.preventDefault();
      } else if (e.key === 'f') {
        // `f` focuses the note for a needs-review submission. The note
        // is required for both reject and needs_review; we treat `r`
        // and `f` as alternative ways to land in the textarea since the
        // intent (record a note, then pick a non-approve disposition)
        // is the same.
        noteRef.current?.focus();
        e.preventDefault();
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [reviewable, pendingMutation, approve]);

  const evidence = finding.evidence;
  const supplierName = stringOrNull(evidence['supplier.name']);
  const glAccount = stringOrNull(evidence['gl_account.name']);
  const billDate = stringOrNull(evidence['bill_date']);
  const province = stringOrNull(evidence['supplier.province']);
  const transactionType = stringOrNull(evidence['transaction_type']);
  // Currency for the evidence amounts is the finding's currency — the engine
  // copies it off the source transaction so bill + tax + recovery share it.
  const findingCurrency = coerceCurrency(finding.recovery.currency);
  const hasContext =
    tagsFor(finding).length > 0 ||
    finding.auto_carried_forward ||
    Boolean(province) ||
    Boolean(transactionType) ||
    // The expense-source badge below renders for non-'direct' sources, so
    // it must open the context row even when it is the only qualifier.
    Boolean(finding.expense_source && finding.expense_source !== 'direct');
  const hasInvoice = Boolean(invoiceLinks && invoiceLinks.length > 0);

  return (
    <>
      <Card
        className="cursor-pointer overflow-hidden border-2 border-brand-royal/20 shadow-md transition-colors hover:border-brand-royal/40"
        onClick={(e) => {
          // Click-anywhere opens the finding detail — but this card is also
          // the dispositioning surface, so ignore clicks that land on any
          // control (buttons, note, reason picker, amount editor) or the
          // no-nav footer.
          if (!clickTargetIsInteractive(e.target)) goToDetail();
        }}
      >
        <CardHeader className="gap-3">
          {/* Zone 1 — identity: what this finding is + which transaction. */}
          <div className="flex flex-wrap items-center gap-2">
            <RuleCodeChip rule={rule} fallbackRuleId={finding.rule_id} />
            <OutcomeBadge outcome={finding.outcome} />
            <FindingStatusBadge kind="finding" status={finding.status} />
            <Link
              to="/runs/$id/findings/$findingId"
              params={{ id: runId, findingId: finding.finding_id }}
              className="ml-auto font-mono text-xs text-brand-slate underline-offset-4 hover:text-brand-royal hover:underline"
              aria-label={`Open detail for this finding on transaction ${finding.transaction_id}`}
            >
              tx {finding.transaction_id}
            </Link>
          </div>

          {/* Zone 2 — context: muted secondary qualifiers, only when present. */}
          {hasContext && (
            <div className="flex flex-wrap items-center gap-2">
              <FindingTags finding={finding} />
              <CarriedForwardPill finding={finding} />
              {province && (
                <Badge variant="outline" className="font-normal">
                  {province}
                </Badge>
              )}
              {transactionType && (
                <Badge variant="secondary" className="font-normal">
                  {transactionType}
                </Badge>
              )}
              {/* Expense source (dext / brex / ramp) — omit the uninformative
                  'direct' default so it doesn't clutter every ledger finding. */}
              {finding.expense_source && finding.expense_source !== 'direct' && (
                <Badge variant="outline" className="font-normal">
                  {finding.expense_source.charAt(0).toUpperCase() +
                    finding.expense_source.slice(1)}
                </Badge>
              )}
            </div>
          )}

          {/* Zone 3 — invoice: the attachment, only when the transaction
              has an invoice attached. */}
          {hasInvoice && (
            <div className="grid gap-1.5 rounded-md border border-border/60 bg-muted/20 px-2.5 py-2">
              <p className="text-[11px] font-semibold uppercase tracking-wide text-brand-slate/70">
                Invoice
              </p>
              <div className="flex flex-wrap items-center gap-2">
                <AttachmentChip
                  links={invoiceLinks}
                  extractions={invoiceExtractions}
                  customerSlug={cardCustomerSlug}
                />
              </div>
            </div>
          )}
          {!hasInvoice && cardCustomerSlug && (
            <div className="flex flex-wrap items-center gap-2">
              {isMarkedNoInvoice ? (
                <>
                  <span
                    className="inline-flex items-center rounded border border-border bg-muted/40 px-2 py-0.5 text-xs font-medium text-brand-slate"
                    title="This transaction has no supporting invoice"
                  >
                    No invoice available
                  </span>
                  {canReview && (
                    <Button
                      type="button"
                      size="sm"
                      variant="ghost"
                      disabled={noInvoiceMark.isPending}
                      onClick={() => noInvoiceMark.mutate(false)}
                    >
                      Undo
                    </Button>
                  )}
                </>
              ) : (
                canReview && (
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    disabled={noInvoiceMark.isPending}
                    onClick={() => noInvoiceMark.mutate(true)}
                    title="Record that this transaction has no supporting invoice — takes it off the collection worklist."
                  >
                    Mark: no invoice available
                  </Button>
                )
              )}
              {noInvoiceMark.isError && (
                <span className="text-[11px] text-destructive">
                  Couldn&rsquo;t update — try again
                </span>
              )}
            </div>
          )}
          <DispositionHistory findingId={finding.finding_id} status={finding.status} />
          <CardTitle>
            <RecoveryAmountInline
              finding={finding}
              canReview={canReview}
              runId={runId}
              textClassName="font-mono text-3xl tabular-nums text-brand-navy"
              layout="block"
            />
            <span className="ml-2 text-sm font-normal text-brand-slate">
              recoverable
            </span>
          </CardTitle>
          {supplierName && (
            <div className="text-sm">
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-xs uppercase tracking-wide text-brand-slate">
                  Supplier
                </span>
                <button
                  type="button"
                  onClick={() => onOpenSupplier(supplierName, finding.supplier_id)}
                  title="See all findings, transactions, and classification for this supplier"
                  className="text-left text-base font-medium text-brand-navy underline-offset-4 hover:text-brand-royal hover:underline focus:outline-none focus-visible:underline"
                >
                  {supplierName}
                </button>
                <button
                  type="button"
                  onClick={() => onOpenSupplier(supplierName, finding.supplier_id)}
                  title={`View all findings + transactions for ${supplierName}`}
                  className="inline-flex items-center gap-1 rounded-md border border-input bg-background px-2 py-0.5 text-xs font-medium text-brand-royal shadow-xs hover:bg-muted/50 hover:text-brand-navy focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-royal"
                >
                  <Eye className="h-3.5 w-3.5" aria-hidden="true" />
                  View supplier
                </button>
              </div>
            </div>
          )}
        </CardHeader>

        <CardContent className="grid gap-5">
          {rulesError && !rule && (
            <p
              className="rounded border border-destructive/40 bg-destructive/10 px-3 py-2 text-sm text-destructive"
              role="status"
            >
              Couldn't load the rule catalog — recovery math context
              isn't available for this finding. You can still approve or
              reject below.
            </p>
          )}

          <FindingSummary
            billAmount={stringOrNull(evidence['bill_amount'])}
            taxAmount={stringOrNull(evidence['tax_amount'])}
            province={province}
            billDate={billDate}
            transactionType={transactionType}
            glAccount={glAccount}
            memo={finding.memo_text?.trim() ? finding.memo_text.trim() : null}
            currency={findingCurrency}
          />

          <ClaimedGstPanel
            claims={claims}
            periodCount={claimPeriodCount}
            ready={claimsReady}
            observedTax={stringOrNull(evidence['tax_amount'])}
            currency={findingCurrency}
          />

          <JournalEntryPanel
            runId={runId}
            transactionId={finding.transaction_id}
            transactionIdUuid={finding.transaction_id_uuid}
          />

          {(finding.approval_note ||
            finding.rejection_reason ||
            finding.review_note ||
            finding.reviewer_alias) && (
            <section className="rounded-md border border-border bg-muted/30 px-3 py-2">
              <h4 className="mb-1 text-xs font-semibold uppercase tracking-wide text-brand-slate">
                Review notes
              </h4>
              {finding.reviewer_alias && (
                <p className="text-sm">
                  <span className="text-brand-slate">
                    {finding.status === 'approved'
                      ? 'Approved by'
                      : finding.status === 'rejected'
                        ? 'Rejected by'
                        : finding.status === 'needs_review'
                          ? 'Flagged by'
                          : 'Reviewed by'}
                    :
                  </span>{' '}
                  <span className="font-medium text-brand-navy">
                    {finding.reviewer_alias}
                  </span>
                </p>
              )}
              {finding.approval_note && (
                <p className="text-sm">
                  <span className="text-brand-slate">Approval note:</span>{' '}
                  <span className="text-brand-navy">{finding.approval_note}</span>
                </p>
              )}
              {finding.rejection_reason && (
                <p className="text-sm">
                  <span className="text-brand-slate">Rejection reason:</span>{' '}
                  <span className="text-brand-navy">{finding.rejection_reason}</span>
                </p>
              )}
              {finding.reason_codes && finding.reason_codes.length > 0 && (
                <div className="mt-1 flex flex-wrap items-baseline gap-2 text-sm">
                  <span className="text-brand-slate">Codes:</span>
                  <ReasonCodePills codes={finding.reason_codes} />
                </div>
              )}
              {finding.review_note && (
                <p className="text-sm">
                  <span className="text-brand-slate">Needs-review note:</span>{' '}
                  <span className="text-brand-navy">{finding.review_note}</span>
                </p>
              )}
            </section>
          )}

          {!rule && !rulesLoading && !rulesError && (
            <p className="text-xs text-brand-slate">
              Rule {finding.rule_id}@v{finding.rule_version} isn't in the
              catalog — it may have been removed or renamed since this run
              completed.
            </p>
          )}
        </CardContent>

        <CardFooter className="flex-col items-stretch gap-3" data-no-cardnav>
          {reviewable ? (
            <>
              <ReasonCodesPicker
                value={rejectCodes}
                onChange={setRejectCodes}
                disabled={pendingMutation}
              />
              <textarea
                ref={noteRef}
                value={note}
                onChange={(e) => setNote(e.target.value)}
                onKeyDown={(e) => {
                  // ⌘/Ctrl+Enter submits — rejects if a reason is typed,
                  // approves if empty. Enter alone inserts a newline so
                  // the textarea behaves normally for multi-line notes.
                  if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
                    e.preventDefault();
                    if (pendingMutation) return;
                    if (canSubmitReject) {
                      reject.mutate();
                    } else {
                      approve.mutate();
                    }
                  }
                }}
                placeholder="Optional for approve + reject (when codes are picked) — required for needs-review (⌘/Ctrl+Enter approves or rejects)"
                maxLength={256}
                disabled={pendingMutation}
                className="min-h-16 w-full rounded-md border border-input bg-transparent px-3 py-2 text-sm shadow-xs outline-none focus-visible:ring-2 focus-visible:ring-brand-royal"
              />
              <div className="flex items-center justify-end gap-2">
                {(approve.isError ||
                  reject.isError ||
                  needsReview.isError) && (
                  <span className="mr-auto text-xs text-destructive">
                    {approve.isError
                      ? 'approve'
                      : reject.isError
                        ? 'reject'
                        : 'needs-review'}{' '}
                    failed — see console
                  </span>
                )}
                <Button
                  type="button"
                  variant="cautionOutline"
                  disabled={pendingMutation || !canSubmitNeedsReview}
                  onClick={() => needsReview.mutate()}
                  title={
                    canSubmitNeedsReview
                      ? 'Flag this finding for a follow-up review'
                      : 'Add a note explaining why this needs review'
                  }
                >
                  {needsReview.isPending ? (
                    'Flagging…'
                  ) : (
                    <>
                      Needs review{' '}
                      <kbd className="ml-1.5 rounded border border-current/30 bg-background/40 px-1 font-mono text-[10px] opacity-75">
                        f
                      </kbd>
                    </>
                  )}
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  disabled={pendingMutation || !canSubmitReject}
                  onClick={() => reject.mutate()}
                  title={
                    canSubmitReject
                      ? 'Press r to focus the reason textarea'
                      : 'Add a reason to enable reject'
                  }
                >
                  {reject.isPending ? (
                    'Rejecting…'
                  ) : (
                    <>
                      Reject{' '}
                      <kbd className="ml-1.5 rounded border border-current/30 bg-background/40 px-1 font-mono text-[10px] opacity-75">
                        r
                      </kbd>
                    </>
                  )}
                </Button>
                <Button
                  type="button"
                  disabled={pendingMutation}
                  onClick={() => approve.mutate()}
                  title="Press a to approve without a note"
                >
                  {approve.isPending ? (
                    'Approving…'
                  ) : (
                    <>
                      Approve{' '}
                      <kbd className="ml-1.5 rounded border border-white/30 bg-white/10 px-1 font-mono text-[10px] opacity-80">
                        a
                      </kbd>
                    </>
                  )}
                </Button>
              </div>
              {supplierName && (pendingBySupplier.get(supplierName) ?? 0) >= 2 && (
                <div className="flex flex-wrap items-center justify-end gap-2 border-t border-border/60 pt-3">
                  <span className="mr-auto text-xs text-brand-slate">
                    <span className="font-mono tabular-nums text-brand-navy">
                      {pendingBySupplier.get(supplierName)}
                    </span>{' '}
                    pending from {supplierName}
                  </span>
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    className="border-amber-500/50 text-amber-900 hover:bg-amber-50"
                    disabled={pendingMutation}
                    onClick={() => onBulkForSupplier(supplierName, 'needs_review')}
                  >
                    Flag all from {supplierName}
                  </Button>
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    disabled={pendingMutation}
                    onClick={() => onBulkForSupplier(supplierName, 'reject')}
                  >
                    Reject all from {supplierName}
                  </Button>
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    disabled={pendingMutation}
                    onClick={() => onBulkForSupplier(supplierName, 'approve')}
                  >
                    Approve all from {supplierName}
                  </Button>
                </div>
              )}
            </>
          ) : (
            <p className="text-sm text-brand-slate">
              {finding.status === 'needs_review'
                ? 'Flagged for review'
                : `Already ${finding.status}`}{' '}
              — use ← / → to navigate.
            </p>
          )}
        </CardFooter>
      </Card>
    </>
  );
}

/** Rule code (`NS01`, `AA01`, …) with a click-to-open-wiki link and a
 *  hover popover showing the rule's title + summary. Falls back to the
 *  raw rule_id badge if the rule catalog hasn't loaded yet. */
function RuleCodeChip({
  rule,
  fallbackRuleId,
}: {
  rule: RuleOut | null;
  fallbackRuleId: string;
}) {
  if (!rule) {
    return (
      <Badge variant="outline" className="font-mono">
        {fallbackRuleId}
      </Badge>
    );
  }
  return (
    <div className="group relative">
      <a
        href={`/rules#${rule.id}`}
        target="_blank"
        rel="noopener noreferrer"
        className="inline-flex items-center gap-1 rounded-md border border-input bg-background px-2 py-0.5 text-xs font-medium font-mono text-brand-navy hover:bg-muted"
      >
        {rule.aa_code}
      </a>
      <div className="invisible absolute left-0 top-full z-10 mt-1 w-80 rounded-md border border-border bg-popover p-3 text-xs text-popover-foreground shadow-md group-hover:visible">
        <p className="text-sm font-semibold text-brand-navy">{rule.title}</p>
        <p className="mt-1 leading-relaxed text-brand-slate">{rule.summary}</p>
        <p className="mt-2 font-medium text-brand-royal">Open in rule wiki →</p>
      </div>
    </div>
  );
}

function OutcomeBadge({ outcome }: { outcome: string }) {
  if (outcome === 'recovery') {
    return (
      <Badge className="border-transparent bg-emerald-600 text-white hover:bg-emerald-600/90">
        recovery
      </Badge>
    );
  }
  if (outcome === 'compliance_risk') {
    return (
      <Badge className="border-transparent bg-amber-600 text-white hover:bg-amber-600/90">
        compliance risk
      </Badge>
    );
  }
  return <Badge variant="secondary">{outcome}</Badge>;
}

function DispositionHistory({ findingId, status }: { findingId: string; status: string }) {
  const { isSignedIn, userKey, getIdToken } = useAuth();
  const events = useQuery({
    queryKey: ['finding-events', findingId, status, userKey],
    // Only a re-decided finding has a story worth showing; skip the
    // fetch for still-pending rows.
    enabled: isSignedIn && status !== 'pending',
    queryFn: async () => listFindingEvents(findingId, (await getIdToken()) ?? ''),
  });

  const trail = useMemo(() => dispositionTrail(events.data?.items ?? []), [events.data]);
  if (trail.length < 2) return null;

  return (
    <div className="flex flex-wrap items-center gap-1 text-xs text-brand-slate">
      {trail.map((seg, i) => (
        <span key={i} className="flex items-center gap-1">
          {i > 0 && <span aria-hidden="true">→</span>}
          <span
            className={cn(
              'rounded px-1.5 py-0.5',
              seg.toStatus === 'rejected' && 'bg-destructive/10 text-destructive',
              seg.toStatus === 'approved' && 'bg-emerald-50 text-emerald-800',
              seg.toStatus === 'needs_review' && 'bg-amber-50 text-amber-900',
              seg.toStatus === 'pending' && 'bg-muted text-brand-slate',
            )}
          >
            {seg.actor ? `${seg.actor} ` : ''}
            {seg.verb}
            {seg.note ? ` · ${seg.note}` : ''}
          </span>
        </span>
      ))}
    </div>
  );
}

/** CRA expected rates by recipient province. Mirrors the YAML map in
 *  `server-py/rules/gst-rate-gap/v1.yml` — when the rule's rate map
 *  shifts (a province changes its HST), update this in lockstep. */
const _EXPECTED_RATE_BY_PROVINCE: Record<string, number> = {
  ON: 0.13,
  NS: 0.15,
  NB: 0.15,
  NL: 0.15,
  PE: 0.15,
  BC: 0.05,
  AB: 0.05,
  SK: 0.05,
  MB: 0.05,
  QC: 0.05,
  NT: 0.05,
  NU: 0.05,
  YT: 0.05,
};

/** Streamlined 7-field summary above the journal entry. Replaces the
 *  prior predicate + math + full-evidence breakdown — Andrew's review
 *  workflow only needs amount / tax / rates / province / date / type
 *  to decide approve vs reject. */
function FindingSummary({
  billAmount,
  taxAmount,
  province,
  billDate,
  transactionType,
  glAccount,
  memo,
  currency,
}: {
  billAmount: string | null;
  taxAmount: string | null;
  province: string | null;
  billDate: string | null;
  transactionType: string | null;
  glAccount: string | null;
  memo: string | null;
  currency: Currency;
}) {
  const billNum = billAmount != null ? Number(billAmount) : null;
  const taxNum = taxAmount != null ? Number(taxAmount) : null;
  const effectiveRate =
    billNum != null && taxNum != null && billNum > 0 ? taxNum / billNum : null;
  const expectedRate =
    province != null ? _EXPECTED_RATE_BY_PROVINCE[province] ?? null : null;

  return (
    <dl className="grid grid-cols-2 gap-x-6 gap-y-2 text-sm sm:grid-cols-3 lg:grid-cols-4">
      <SummaryField label="Amount paid" value={formatCurrencyOrDash(billNum, currency)} mono />
      <SummaryField label="Tax paid" value={formatCurrencyOrDash(taxNum, currency)} mono />
      <SummaryField
        label="Effective rate"
        value={formatPercentOrDash(effectiveRate)}
        mono
      />
      <SummaryField
        label="Expected rate"
        value={formatPercentOrDash(expectedRate)}
        mono
      />
      <SummaryField label="Province" value={province ?? '—'} />
      <SummaryField label="Date" value={billDate ?? '—'} mono />
      <SummaryField label="Transaction type" value={transactionType ?? '—'} />
      {glAccount && (
        <div className="col-span-2 sm:col-span-3 lg:col-span-4">
          <dt className="text-xs uppercase tracking-wide text-brand-slate">
            GL account
          </dt>
          <dd className="mt-0.5 text-brand-navy">
            <GlAccountChip glAccount={glAccount} />
          </dd>
        </div>
      )}
      {memo && (
        <div className="col-span-2 sm:col-span-3 lg:col-span-4">
          <dt className="text-xs uppercase tracking-wide text-brand-slate">Memo</dt>
          <dd className="mt-0.5 whitespace-pre-wrap text-brand-navy">{memo}</dd>
        </div>
      )}
    </dl>
  );
}

/** The finding card's GL-account value, linked into the classification
 *  workbench when the run has customer context (CLI runs don't). */
function GlAccountChip({ glAccount }: { glAccount: string }) {
  const customerSlug = useContext(RunCustomerSlugContext);
  if (!customerSlug) return <>{glAccount}</>;
  return (
    <Link
      to="/customers/$slug"
      params={{ slug: customerSlug }}
      search={{ tab: 'accounts', account: glAccount }}
      title="Open in the classification workbench"
      className="underline-offset-4 hover:text-brand-royal hover:underline"
    >
      {glAccount}
    </Link>
  );
}

function SummaryField({
  label,
  value,
  mono = false,
}: {
  label: string;
  value: string;
  mono?: boolean;
}) {
  return (
    <div>
      <dt className="text-xs uppercase tracking-wide text-brand-slate">{label}</dt>
      <dd
        className={cn(
          'mt-0.5 text-brand-navy',
          mono && 'font-mono tabular-nums',
        )}
      >
        {value}
      </dd>
    </div>
  );
}

function formatCurrencyOrDash(n: number | null, currency: Currency = 'CAD'): string {
  if (n == null || Number.isNaN(n)) return '—';
  // Use `formatCurrency` for the suffix-style render — the
  // `style: 'currency'` Intl path produces "$X" without an ISO suffix,
  // which is the very ambiguity Slice 1 / 2 set out to remove.
  return formatCurrency(n, currency);
}

function formatPercentOrDash(n: number | null): string {
  if (n == null || Number.isNaN(n)) return '—';
  return `${(n * 100).toFixed(2)}%`;
}
